import { api } from "@/lib/api";

export type EscalaLetter = "E" | "S" | "C" | "A" | "L" | "A2";
export type EscalaScores = Record<EscalaLetter, number>;
export type EscalaBusinessModel = "direct" | "consultative" | "hybrid";

// ─────────────────────────────────────────────────────────────
// Questionário v1.0
// ─────────────────────────────────────────────────────────────

export type AlternativeOption = {
  key: "A" | "B" | "C" | "D" | "E";
  text: string;
  points: 0 | 25 | 45 | 65 | 85;
  level: 1 | 2 | 3 | 4 | 5;
};

export type RoutingOption = {
  key: string;
  text: string;
  mapsTo: EscalaBusinessModel;
};

export type RoutingQuestion = {
  type: "routing";
  key: string;
  letter: "P0";
  weight: 0;
  prompt: string;
  options: RoutingOption[];
};

export type AlternativeQuestion = {
  type: "alternative";
  key: string;
  letter: EscalaLetter;
  weight: number;
  prompt: string;
  options: AlternativeOption[];
  routed?: boolean;
};

export type EssayRubricItem = { id: number; criterion: string };

export type EssayQuestion = {
  type: "essay";
  key: string;
  letter: EscalaLetter;
  weight: number;
  prompt: string;
  rubric: EssayRubricItem[];
};

export type EscalaQuestion = RoutingQuestion | AlternativeQuestion | EssayQuestion;

export type EscalaQuestionnaireResponse = {
  version: string;
  model: EscalaBusinessModel | null;
  questions: EscalaQuestion[];
  letterNames: Record<EscalaLetter, string>;
};

// ─────────────────────────────────────────────────────────────
// Submit
// ─────────────────────────────────────────────────────────────

export type ResponseInput = {
  questionKey: string;
  optionKey?: string;
  essayText?: string;
};

export type EscalaAnswer = {
  questionKey: string;
  type: "routing" | "alternative" | "essay";
  weight: number;
  optionKey?: string;
  essayText?: string;
  criteriaMet?: string[];
  aiJustification?: string;
  points: number;
};

export type EscalaRecommendation = {
  letter: EscalaLetter;
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
};

export type EscalaAssessment = {
  id: string;
  companyId: string;
  projectId: string | null;
  answers: EscalaAnswer[];
  scores: EscalaScores;
  strongestLetters: EscalaLetter[];
  weakestLetters: EscalaLetter[];
  recommendations: EscalaRecommendation[];
  versionString: string;
  businessModel: EscalaBusinessModel | null;
  profileText: string | null;
  costUsdMillis: number;
  completedAt: string;
  createdAt: string;
};

// ─────────────────────────────────────────────────────────────
// Recomendações ("Comece por aqui")
// ─────────────────────────────────────────────────────────────

export type RecommendationStatus = "gap_critical" | "gap_atacar" | "saudavel";
export type RecommendationCategory = "diagnóstico" | "ataque" | "estrutura";

export type EscalaTaskRecommendation = {
  letter: EscalaLetter;
  letterName: string;
  score: number;
  status: RecommendationStatus;
  templates: Array<{
    templateKey: string;
    title: string;
    weight: number;
    finalScore: number;
    category: RecommendationCategory;
    reason: { why: string; expectedOutcome: string };
    requiresIntegrationKey: string | null;
    isLocked: boolean;
  }>;
};

export type EscalaRecommendationsResponse =
  | { hasAssessment: false; recommendations: [] }
  | {
      hasAssessment: true;
      assessmentCompletedAt: string;
      scores: EscalaScores;
      profileText: string | null;
      recommendations: EscalaTaskRecommendation[];
    };

export const escalaApi = {
  questionnaire: (model?: EscalaBusinessModel | null) => {
    const qs = model ? `?model=${model}` : "";
    return api.get<EscalaQuestionnaireResponse>(`/escala/questionnaire${qs}`);
  },
  latest: (companyId: string) =>
    api.get<EscalaAssessment | null>(`/companies/${companyId}/escala/latest`),
  history: (companyId: string) =>
    api.get<EscalaAssessment[]>(`/companies/${companyId}/escala`),
  recommendations: (companyId: string) =>
    api.get<EscalaRecommendationsResponse>(
      `/companies/${companyId}/escala/recommendations`,
    ),
  submit: (
    companyId: string,
    input: {
      businessModel: EscalaBusinessModel | null;
      responses: ResponseInput[];
      projectId?: string | null;
    },
  ) =>
    api.post<EscalaAssessment>(
      `/companies/${companyId}/escala/submit`,
      input,
    ),
};
